import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Clock, DollarSign, Target, TrendingUp, CheckCircle, Zap, Users, Settings } from "lucide-react";

const UKPage = () => {
  const painPoints = [
    {
      icon: <DollarSign className="h-8 w-8 text-orange-500" />,
      title: "Wasted Google Ads Budget",
      description: "You're paying for keywords that don't convert. Many UK businesses lose 40-60% of their budget on inefficient campaigns."
    },
    {
      icon: <Target className="h-8 w-8 text-orange-500" />,
      title: "Wrong Keyword Strategy",
      description: "UK keywords have specific requirements. Generic targeting tools don't work for our B2B market."
    },
    {
      icon: <Clock className="h-8 w-8 text-orange-500" />,
      title: "Too Much Time to Setup",
      description: "Google Ads is complex. Many agencies take weeks to set up whilst you wait for results."
    }
  ];

  const features = [
    {
      icon: <Zap className="h-8 w-8 text-blue-500" />,
      title: "Budget-Smart Campaigns",
      description: "AI-driven keyword filtering for UK markets. Pay only for highly qualified inquiries."
    },
    {
      icon: <Target className="h-8 w-8 text-blue-500" />,
      title: "Local Keyword Analysis",
      description: "Real Google Ads bids for UK keywords. Understand exactly what each keyword costs and delivers."
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-blue-500" />,
      title: "Automatic Optimisation",
      description: "Campaigns adjust daily. Better conversion rates with less manual effort."
    },
    {
      icon: <Users className="h-8 w-8 text-blue-500" />,
      title: "UK-Based Support",
      description: "Full support from UK team. No language barriers for technical questions."
    }
  ];

  const steps = [
    {
      number: "1",
      title: "Tell Us About Your Business",
      description: "Share your business details, target customers and budget. Just 2 minutes to get started."
    },
    {
      number: "2",
      title: "AI Creates Your Campaigns",
      description: "Upfixe analyses your website and creates professional, UK-optimised campaigns."
    },
    {
      number: "3",
      title: "More Customers, Less Waste",
      description: "See highly qualified inquiries. Budget intelligence means faster results."
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Google Ads for UK Businesses | Budget-Smart Automation | Upfixe</title>
        <meta name="description" content="Budget-intelligent Google Ads for UK businesses. Smart keyword filtering, local optimisation, UK support. Start free today." />
        <meta name="keywords" content="Google Ads UK, Google Ads Agency, Keyword Optimisation, Budget Optimisation, UK Campaigns" />
        <meta property="og:title" content="Google Ads for UK Businesses | Budget-Smart Automation | Upfixe" />
        <meta property="og:description" content="Budget-intelligent Google Ads for UK businesses. Smart keyword filtering, local optimisation, UK support." />
        <link rel="canonical" href="https://upfixe.com/uk" />
        <link rel="alternate" hrefLang="x-default" href="https://upfixe.com/" />
        <link rel="alternate" hrefLang="en" href="https://upfixe.com/" />
        <link rel="alternate" hrefLang="de" href="https://upfixe.com/de" />
        <link rel="alternate" hrefLang="fr" href="https://upfixe.com/fr" />
        <link rel="alternate" hrefLang="en-GB" href="https://upfixe.com/uk" />
        <link rel="alternate" hrefLang="en-AU" href="https://upfixe.com/au" />
      </Helmet>
      <Header />

      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          {/* Hero Section */}
          <div className="text-center mb-20">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              Stop Wasting Budget on <br />
              <span className="text-blue-600">Keywords That Don't Convert</span>
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              Google Ads for UK businesses that actually work. AI-powered budget intelligence. No expensive agencies. No fluff.
            </p>
            <div className="flex justify-center mb-6">
              <Button
                size="lg"
                className="text-lg px-8 h-14 bg-blue-600 hover:bg-blue-700 font-semibold"
                asChild
              >
                <Link to="/auth?signup=true">Start Free</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">No Credit Card Required • UK Support • Professional Results</p>
          </div>

          {/* Problem Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">UK Businesses: Why Google Ads is Difficult</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                UK keywords are expensive. Generic targeting tools don't work for our B2B market.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {painPoints.map((point, index) => (
                <Card key={index} className="text-center p-6 border-orange-200 bg-orange-50/50">
                  <div className="flex justify-center mb-4">{point.icon}</div>
                  <h3 className="text-xl font-semibold mb-3">{point.title}</h3>
                  <p className="text-muted-foreground">{point.description}</p>
                </Card>
              ))}
            </div>
          </div>

          {/* Solution Section */}
          <div className="mb-20 bg-blue-50 p-12 rounded-2xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold mb-6">Upfixe: Budget Intelligence for the UK</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Real Google Ads bids. UK keyword intelligence. Automatic budget optimisation that works.
              </p>
            </div>
          </div>

          {/* Features Section */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Why UK Agencies Choose Upfixe</h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                Smart tools for UK markets. No complexity. Just results.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {features.map((feature, index) => (
                <Card key={index} className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                      {feature.icon}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                      <p className="text-muted-foreground">{feature.description}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          {/* How It Works */}
          <div className="mb-20">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Get Started in 3 Steps</h2>
              <p className="text-xl text-muted-foreground">
                From setup to qualified leads in less than 30 minutes.
              </p>
            </div>

            <div className="max-w-4xl mx-auto">
              {steps.map((step, index) => (
                <div key={index} className={`flex flex-col md:flex-row gap-6 items-center mb-12 ${index === steps.length - 1 ? 'mb-0' : ''}`}>
                  <div className="flex-shrink-0 w-16 h-16 rounded-full bg-blue-600 flex items-center justify-center text-white text-2xl font-bold">
                    {step.number}
                  </div>
                  <div className="text-center md:text-left">
                    <h3 className="text-2xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-lg text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Final CTA */}
          <div className="text-center bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-12 rounded-2xl">
            <h2 className="text-4xl font-bold mb-4">Ready for Smart Google Ads?</h2>
            <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
              Thousands of UK business owners are already automating their Google Ads. No agencies. No complexity. Just results.
            </p>
            <div className="flex justify-center mb-6">
              <Button
                size="lg"
                className="text-lg px-8 h-14 bg-white text-blue-600 hover:bg-gray-100 font-semibold"
                asChild
              >
                <Link to="/auth?signup=true">Try Free Today</Link>
              </Button>
            </div>
            <p className="text-sm opacity-80">Simple Setup • Professional Campaigns • UK Support</p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default UKPage;
